import { Icon, token } from "@may-i/mash-design-system";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import {
  useEventSourceKeyWhitelist,
  useUpdateEventSourceKeyWhitelist,
} from "~/features/analysis-config/hooks/use-event-source-key-whitelist";
import { useUndoStack } from "~/shared/hooks/use-undo-stack";

interface WhitelistTabProps {
  clientUid?: string;
}

export function WhitelistTab({ clientUid }: WhitelistTabProps) {
  const { t } = useTranslation("resource");
  const { data: serverKeywords } = useEventSourceKeyWhitelist(clientUid);
  const updateMut = useUpdateEventSourceKeyWhitelist(clientUid);

  const [keywords, setKeywords] = useState<string[]>([]);
  const [inputValue, setInputValue] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const keywordsRef = useRef(keywords);
  keywordsRef.current = keywords;
  const initializedRef = useRef(false);

  const { push: pushUndo, undo } = useUndoStack({
    keywords: (value: string[]) => {
      setKeywords(value);
      if (clientUid) updateMut.mutate(value);
    },
  });

  useEffect(() => {
    if (serverKeywords && serverKeywords.length > 0 && !initializedRef.current) {
      setKeywords(serverKeywords);
      initializedRef.current = true;
    }
  }, [serverKeywords]);

  function addKeyword(raw: string) {
    const value = raw.trim().toLowerCase();
    if (!value) return;
    const current = keywordsRef.current;
    if (current.includes(value)) return;
    pushUndo({ keywords: current });
    const next = [...current, value];
    setKeywords(next);
    setInputValue("");
    if (clientUid) updateMut.mutate(next);
  }

  function removeKeyword(keyword: string) {
    pushUndo({ keywords: keywordsRef.current });
    const next = keywordsRef.current.filter((k) => k !== keyword);
    setKeywords(next);
    if (clientUid) updateMut.mutate(next);
  }

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "z" && !e.shiftKey) {
        if (inputRef.current?.contains(e.target as Node) || e.target === inputRef.current) {
          e.preventDefault();
          undo();
        }
      }
    };
    window.addEventListener("keydown", handler, true);
    return () => window.removeEventListener("keydown", handler, true);
  }, [undo]);

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.nativeEvent.isComposing) return;
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addKeyword(inputValue);
    } else if (e.key === "Backspace" && inputValue === "" && keywordsRef.current.length > 0) {
      removeKeyword(keywordsRef.current[keywordsRef.current.length - 1]);
    }
  }

  return (
    <Wrapper>
      <InfoBanner>
        <InfoIcon>
          <Icon icon="ic_information" width={16} />
        </InfoIcon>
        <InfoText>{t("whitelist.info")}</InfoText>
      </InfoBanner>
      <TagArea onClick={() => inputRef.current?.focus()}>
        {keywords.map((keyword) => (
          <Tag key={keyword}>
            <TagText>{keyword}</TagText>
            <TagRemoveBtn
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={(e) => {
                e.stopPropagation();
                removeKeyword(keyword);
              }}
            >
              <Icon icon="ic_remove" width={16} />
            </TagRemoveBtn>
          </Tag>
        ))}
        <InputWrap onClick={(e) => e.stopPropagation()}>
          <InlineInput
            ref={inputRef}
            type="text"
            autoComplete="off"
            value={inputValue}
            placeholder={t("whitelist.placeholder")}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
          />
        </InputWrap>
      </TagArea>
      <ShortcutHint>{t("whitelist.shortcutHint")}</ShortcutHint>
    </Wrapper>
  );
}

// --- Styles ---

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ShortcutHint = styled.span`
  align-self: flex-end;
  margin-top: -4px;
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 1.5;
  color: ${token("colors.text.secondary")};
`;

const InfoBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  background: #fafbff;
  border-radius: 8px;
`;

const InfoIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${token("colors.semantics.info.main")};
`;

const InfoText = styled.span`
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.54;
  color: ${token("colors.text.primary")};
`;

const TagArea = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  align-content: flex-start;
  gap: 8px;
  min-height: 300px;
  padding: 16px;
  border-radius: 8px;
  border: 1px solid rgba(143, 149, 178, 0.24);
  background: #fff;
  cursor: text;
`;

const Tag = styled.div`
  display: inline-flex;
  align-items: center;
  padding: 0 4px;
  border-radius: 1000px;
  background: #e6e8f0;
`;

const TagText = styled.span`
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 1.5;
  padding: 2px 4px;
  color: #11111f;
`;

const TagRemoveBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  color: rgba(143, 149, 178, 0.48);

  &:hover {
    color: ${token("colors.text.secondary")};
  }
`;

const InputWrap = styled.div`
  display: flex;
  flex: 1;
  min-width: 80px;
`;

const InlineInput = styled.input`
  border: none;
  outline: none;
  background: transparent;
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 24px;
  height: 24px;
  color: #11111f;
  width: 100%;
  padding: 0;

  &::placeholder {
    color: rgba(143, 149, 178, 0.6);
  }
`;
